import { useState, useEffect, useCallback } from "react";

interface RouterState {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

function parseUrl(): RouterState {
  const path = window.location.pathname;
  const query = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};

  return { path, params, query };
}

export function useRouter() {
  const [state, setState] = useState<RouterState>(parseUrl);

  useEffect(() => {
    const handlePopState = () => {
      setState(parseUrl());
      // Scroll to top on browser back/forward navigation
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean; scroll?: boolean }) => {
    const { replace = false, scroll = true } = options || {};

    if (replace) {
      window.history.replaceState({}, "", to);
    } else {
      window.history.pushState({}, "", to);
    }
    setState(parseUrl());

    // Scroll to top on navigation (unless explicitly disabled)
    if (scroll) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const getParam = useCallback((name: string) => {
    return state.query.get(name);
  }, [state.query]);

  return {
    path: state.path,
    query: state.query,
    navigate,
    getParam,
  };
}

export function matchRoute(path: string, pattern: string): { match: boolean; params: Record<string, string> } {
  const pathParts = path.split("/").filter(Boolean);
  const patternParts = pattern.split("/").filter(Boolean);

  if (pathParts.length !== patternParts.length) {
    return { match: false, params: {} };
  }

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (patternPart.startsWith(":")) {
      params[patternPart.slice(1)] = pathPart;
    } else if (patternPart !== pathPart) {
      return { match: false, params: {} };
    }
  }

  return { match: true, params };
}
